import { TravelInsuranceRange, TravelInsuranceType } from "../apiGraphql/types/types";

/**
 * Central, feature-flag driven config for product specific promo behaviour.
 *
 * Everything here is data: to enable/disable a behaviour flip `enabled`, to
 * target another product add a new entry to the relevant map. No component code
 * needs to change. Built generic on purpose so it can be reused for future
 * products / brands / insurance types.
 */

/* -------------------------------------------------------------------------- */
/* 1. Default base coverage range                                             */
/* -------------------------------------------------------------------------- */

export interface DefaultCoverageRangeRule {
  enabled: boolean;
  /** Insurance type this default applies to (e.g. yearly). */
  insuranceType: TravelInsuranceType;
  /** Coverage range to pre-select when a new SO of this type is created. */
  defaultRange: TravelInsuranceRange;
}

/**
 * When a new SO is created its base coverage range defaults according to the
 * first matching enabled rule below. If nothing matches the caller supplied
 * fallback is used.
 */
export const defaultCoverageRangeRules: DefaultCoverageRangeRule[] = [
  /* {
     enabled: true,
     insuranceType: TravelInsuranceType.Year,
     defaultRange: TravelInsuranceRange.ForthyToSixty,
   }, */
];

export const getDefaultCoverageRange = (
  insuranceType?: TravelInsuranceType,
  fallback: TravelInsuranceRange = TravelInsuranceRange.TenTwenty
): TravelInsuranceRange => {
  const rule = defaultCoverageRangeRules.find(
    (r) => r.enabled && r.insuranceType === insuranceType
  );
  return rule?.defaultRange ?? fallback;
};

/* -------------------------------------------------------------------------- */
/* 2. Promo price display on offer cards                                      */
/* -------------------------------------------------------------------------- */

export type PromoDisplayMode =
  // Try a crossed-out "before" price (totalPrice * multiplier); if that can't
  // be produced, fall back to just the badge ("AKCIJSKA CIJENA").
  | "crossedOutMultiplier"
  // Always show only the badge next to the price.
  | "badge";

export interface PromoPriceRule {
  enabled: boolean;
  /** Brand codes this rule targets (e.g. ['uniqa']). */
  brandCodes: string[];
  /** Insurance types this rule targets (e.g. yearly). */
  insuranceTypes: TravelInsuranceType[];
  /** Coverage ranges this rule targets (e.g. the top two ranges). */
  insuranceRanges: TravelInsuranceRange[];
  mode: PromoDisplayMode;
  /** Multiplier for `crossedOutMultiplier` mode. */
  multiplier?: number;
  /** i18n key for the badge text (badge mode + crossedOutMultiplier fallback). */
  badgeTextKey?: string;
}

export const promoPriceRules: PromoPriceRule[] = [
  /*{
    enabled: true,
    brandCodes: ["uniqa"],
    insuranceTypes: [TravelInsuranceType.Year],
    insuranceRanges: [
      TravelInsuranceRange.ForthyToSixty,
      TravelInsuranceRange.MoreThanSixty,
    ],
    // Preferred: crossed-out price = totalPrice * 2. If the price can't be
    // computed it falls back to the "AKCIJSKA CIJENA" badge automatically.
    // Switch `mode` to "badge" to force the badge-only behaviour instead.
    mode: "crossedOutMultiplier",
    multiplier: 2,
    badgeTextKey: "components.cards.offerCard.promoPrice",
  }, */
];

export interface PromoResult {
  /** When set, render this crossed-out "before" price next to the total. */
  strikethroughPrice?: number;
  /** When set, render a promo badge with this i18n key. */
  badgeTextKey?: string;
}

export const resolvePromo = (params: {
  brandCode?: string;
  insuranceType?: TravelInsuranceType | null;
  insuranceRange?: TravelInsuranceRange | null;
  totalPrice?: number | null;
}): PromoResult | null => {
  const { brandCode, insuranceType, insuranceRange, totalPrice } = params;

  const rule = promoPriceRules.find(
    (r) =>
      r.enabled &&
      !!brandCode &&
      r.brandCodes.includes(brandCode) &&
      !!insuranceType &&
      r.insuranceTypes.includes(insuranceType) &&
      !!insuranceRange &&
      r.insuranceRanges.includes(insuranceRange)
  );

  if (!rule) return null;

  if (rule.mode === "crossedOutMultiplier") {
    // Preferred behaviour: crossed-out computed price + promo badge.
    if (rule.multiplier && totalPrice) {
      return {
        strikethroughPrice: totalPrice * rule.multiplier,
        badgeTextKey: rule.badgeTextKey,
      };
    }
    // Fallback: not possible to compute a price -> show badge only.
    return { badgeTextKey: rule.badgeTextKey };
  }

  // "badge" mode: badge only.
  return { badgeTextKey: rule.badgeTextKey };
};
